import type { AppDefinition } from '@web/api/types'
import { Icon } from '@web/components/ui/icons'
import { appIcon } from '@web/lib/appIcon'
import { navigateWorkspace } from '@web/state/identity'

export const APP_GROUPS = [
  'Base Agents',
  'Your Agents',
  'Agents',
  'Marketing',
  'Product & Engineering',
  'Ops & Finance',
  'Legal',
  'Admin',
  'Other Agents',
] as const

/** Keep the built-in agent artwork visually distinct inside its shared group. */
const ALTERNATE_COLOR_APP_IDS = new Set([
  'claude-code',
  'codex',
  'opencode',
  'puck',
])

const BASE_AGENT_IDS = new Set(['claude-code', 'codex', 'opencode', 'puck'])
const appGroup = (app: AppDefinition) =>
  BASE_AGENT_IDS.has(app.id) ? 'Base Agents' : (app.group ?? 'Other Agents')

export function groupedApps(apps: AppDefinition[]) {
  const names = [...new Set([...APP_GROUPS, ...apps.map(appGroup)])]
  return names
    .map((name) => ({
      name,
      items: apps
        .filter((app) => app.id !== 'pulse' && appGroup(app) === name)
        .sort(
          (a, b) =>
            (a.galleryOrder ?? 1000) - (b.galleryOrder ?? 1000) ||
            a.id.localeCompare(b.id),
        ),
    }))
    .filter(({ items }) => items.length)
}

/** The ownership glyph: one person for your own app, two for a shared one. */
export function OwnershipGlyph({
  ownership,
  size = 17,
}: {
  ownership: NonNullable<AppDefinition['ownership']>
  size?: number
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx={ownership === 'shared' ? 9 : 12} cy="8" r="3" />
      {ownership === 'shared' ? (
        <path d="M3 21v-2a6 6 0 0 1 12 0v2m2-17a3 3 0 0 1 0 6m1 5a5 5 0 0 1 3 5v1" />
      ) : (
        <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
      )}
    </svg>
  )
}

function OwnershipBadge({
  ownership,
}: {
  ownership?: AppDefinition['ownership']
}) {
  if (!ownership) return null
  return (
    <span
      className={`app-ownership app-ownership-${ownership}`}
      role="img"
      aria-label={
        ownership === 'personal' ? 'Created by you' : 'Shared with you'
      }
      title={ownership === 'personal' ? 'Created by you' : 'Shared with you'}
    >
      <OwnershipGlyph ownership={ownership} />
    </span>
  )
}

export function GalleryGrid({
  apps,
  launching,
  onLaunch,
}: {
  apps: AppDefinition[]
  launching: string | null
  onLaunch: (app: AppDefinition) => void
}) {
  return (
    <div className="app-gallery">
      {groupedApps(apps).map(({ name, items }) => {
        return (
          <section className="app-group" aria-label={name} key={name}>
            <h2 className="app-group-title">{name}</h2>
            <div className="app-grid">
              {items.map((item) => (
                <div
                  className={`app-tile ${
                    ALTERNATE_COLOR_APP_IDS.has(item.id)
                      ? 'app-tile-color-alternate'
                      : 'app-tile-color-default'
                  }`}
                  key={`${item.id}:${item.digest ?? item.version}`}
                >
                  <button
                    type="button"
                    className="app-tile-button"
                    onClick={() => onLaunch(item)}
                    disabled={!!launching}
                    aria-label={`Open ${item.title}`}
                  >
                    <span className="app-tile-artwork">
                      <span className="app-tile-icon">
                        <img
                          src={appIcon(item.iconUrl, 128)}
                          width={96}
                          height={96}
                          decoding="async"
                          alt=""
                        />
                        {launching === item.id && (
                          <span className="home-app-loading">
                            <Icon name="loader" size={30} className="spin" />
                          </span>
                        )}
                      </span>
                      <OwnershipBadge ownership={item.ownership} />
                    </span>
                    <span className="app-tile-title">{item.title}</span>
                  </button>
                </div>
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

/** Preserve the catalogue's layout while its app definitions are loading. */
export function GallerySkeleton() {
  return (
    <div className="app-gallery app-gallery-skeleton" aria-busy="true">
      <span className="sr-only">Loading your agents…</span>
      {Array.from({ length: 2 }, (_, group) => (
        <section className="app-group" aria-hidden="true" key={group}>
          <span className="gallery-skeleton gallery-skeleton-heading" />
          <div className="app-grid">
            {Array.from({ length: 8 }, (_, tile) => (
              <div className="gallery-skeleton-tile" key={tile}>
                <span className="gallery-skeleton gallery-skeleton-icon" />
                <span className="gallery-skeleton gallery-skeleton-label" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

export function AppOverview({ app }: { app: AppDefinition }) {
  return (
    <main className="app-overview">
      <button
        type="button"
        className="btn overview-back"
        onClick={() => navigateWorkspace('/')}
      >
        <span aria-hidden="true">←</span> All agents
      </button>
      <article className="overview-card">
        <span className="app-tile-artwork overview-artwork">
          <img
            className="overview-icon"
            src={appIcon(app.iconUrl, 128)}
            width={96}
            height={96}
            alt=""
          />
          <OwnershipBadge ownership={app.ownership} />
        </span>
        <p className="overview-group">{app.group}</p>
        <h1>{app.title}</h1>
        <span className="overview-status">App preview</span>
        <p className="overview-summary">{app.overview?.summary}</p>
        <h2>What this app will do</h2>
        <ul>
          {app.overview?.capabilities.map((capability) => (
            <li key={capability}>
              <Icon name="check" size={16} />
              <span>{capability}</span>
            </li>
          ))}
        </ul>
        <p className="overview-note">
          This is an overview of a planned app. It does not run an agent or
          process your data yet.
        </p>
      </article>
    </main>
  )
}
